import { Editor, EditorArguments, EditorValidationResult } from 'angular-slickgrid';

function isCheckedValue(value: unknown): boolean {
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue || ['0', 'false', 'no', 'n', 'off'].includes(normalizedValue)) {
      return false;
    }
  }

  return Boolean(value);
}

export class CustomCheckboxEditor implements Editor {
  private readonly args: EditorArguments;
  private input!: HTMLInputElement;
  private label!: HTMLLabelElement;
  private originalValue = false;
  private readonly handleCheckboxKeydown = (event: KeyboardEvent): void => {
    if (event.key !== ' ' && event.key !== 'Spacebar' && event.code !== 'Space') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.input.checked = !this.input.checked;
  };

  constructor(args: EditorArguments) {
    this.args = args;
    this.init();
  }

  init(): void {
    const container = this.args.container;
    const fieldName = this.getFieldName();
    const labelText = this.getLabelText(fieldName);

    this.label = document.createElement('label');
    this.label.className = 'editor-checkbox-label';

    this.input = document.createElement('input');
    this.input.type = 'checkbox';
    this.input.className = ['editor-checkbox', fieldName ? `editor-${fieldName}` : '']
      .filter(Boolean)
      .join(' ');
    this.input.setAttribute('aria-label', `${labelText} Checkbox Editor`);
    this.input.title = '';
    this.input.addEventListener('keydown', this.handleCheckboxKeydown);

    ['mousedown', 'click', 'dblclick'].forEach(eventName => {
      this.label.addEventListener(eventName, event => event.stopPropagation());
      this.input.addEventListener(eventName, event => event.stopPropagation());
    });

    this.label.appendChild(this.input);
    container.appendChild(this.label);
  }

  destroy(): void {
    this.input?.removeEventListener('keydown', this.handleCheckboxKeydown);
    this.label?.remove();
  }

  focus(): void {
    this.input?.focus();
  }

  loadValue(item: any): void {
    const fieldName = this.getFieldName();
    const value = item?.[fieldName];

    this.originalValue = isCheckedValue(value);
    this.input.checked = this.originalValue;
    this.input.focus();
  }

  serializeValue(): boolean {
    return this.input.checked;
  }

  applyValue(item: any, state: boolean): void {
    const fieldName = this.getFieldName();
    item[fieldName] = state;
  }

  isValueChanged(): boolean {
    return this.input.checked !== this.originalValue;
  }

  validate(): EditorValidationResult {
    const column = this.args.column;
    const editorOptions = (column.editor as any) || {};
    const value = this.input.checked;
    const fieldName = this.getFieldName();

    if (editorOptions.required && !value) {
      return {
        valid: false,
        msg: 'This field is required',
      };
    }

    if (editorOptions.validator) {
      const item = this.args.item || {};
      const updatedItem = { ...item, [fieldName]: value };
      const validationArgs = { ...this.args, item: updatedItem };
      return editorOptions.validator(value, validationArgs);
    }

    return { valid: true, msg: '' };
  }

  private getFieldName(): string {
    return typeof this.args.column.field === 'string' ? this.args.column.field.trim() : '';
  }

  private getLabelText(fieldName: string): string {
    return typeof this.args.column.name === 'string' && this.args.column.name.trim()
      ? this.args.column.name.trim()
      : fieldName || 'Checkbox';
  }
}
